import React, { useCallback } from "react";

import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faRotateRight,
  faSpinner,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { useIsMutating } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AnimatePresence, m } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { AgentVersionStatusIndicator } from "src/agents/AgentVersionName";
import { AgentPlaygroundDrawer } from "src/agents/AgentVersionPage/AgentPlaygroundDrawer";
import { AddToolDrawer } from "src/agents/AgentVersionPage/AgentToolsEditor/AddToolDrawer";
import { FlowToolConfigDrawer } from "src/agents/AgentVersionPage/AgentToolsEditor/FlowToolConfigDrawer";
import {
  AgentVersionConfigPanel,
  AgentVersionConfigPanelSkeleton,
} from "src/agents/AgentVersionPage/AgentVersionConfigPanel";
import { AgentVersionConfigPanelProvider } from "src/agents/AgentVersionPage/AgentVersionConfigPanelContext";
import { AgentVersionFormProvider } from "src/agents/AgentVersionPage/AgentVersionFormProvider";
import { JsonSchemaDrawer } from "src/agents/AgentVersionPage/JsonSchemaDrawer";
import {
  PlaygroundProvider,
  usePlaygroundContext,
} from "src/agents/AgentVersionPage/PlaygroundContext";
import {
  DrawerContainer,
  SideDrawerCoordinatorProvider,
  useSideDrawerCoordinator,
} from "src/agents/base/SideDrawer";
import { CreateAgentVersionModal } from "src/agents/CreateAgentVersionModal";
import { EditAgentVersionModal } from "src/agents/EditAgentVersionModal";
import { DeleteAgentVersionModal } from "src/agents/modals/DeleteAgentVersionModal";
import { AgentVersion } from "src/agents/types";
import { useAgentVersionMenuOptions } from "src/agents/useAgentVersionMenuOptions";
import {
  AGENT_VERSION_CONFIGURATION_MUTATION_KEY,
  useAgent,
  useAgentVersion,
  useAgentVersions,
  useDeleteAgentVersion,
  usePublishAgentVersion,
} from "src/api/agents/management";
import { DecisionEnvironment } from "src/api/types";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { DisplayDateFromNow } from "src/base-components/Date";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { AgentVersionConfigurationOutput } from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { DashboardContent } from "src/layout/DashboardContent";
import { PageHeader } from "src/layout/PageHeader";
import { AgentHistoryKeys } from "src/router/SearchParams";
import {
  AgentVersionPageParams,
  DashboardPageParams,
  getUrlToAgentHistoryPage,
  getUrlToAgentsPage,
  getUrlToAgentVersionPage,
  getUrlToAgentVersionsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentVersionPage: React.FC = () => {
  const { versionId } = useParamsDecode<AgentVersionPageParams>();
  return (
    // Use key to force state reset when versionId changes
    <SideDrawerCoordinatorProvider key={versionId}>
      <AgentVersionPageContent />
    </SideDrawerCoordinatorProvider>
  );
};

const AgentVersionPageContent: React.FC = () => {
  useDocumentTitle(["Edit Agent Version", "Agents"]);

  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();
  const navigate = useNavigate();

  const agentQuery = useAgent(agentId);
  const versionsQuery = useAgentVersions(agentId);
  const versionQuery = useAgentVersion(agentId, versionId);
  const publishMutation = usePublishAgentVersion();

  const {
    isOpen: isDuplicateModalOpen,
    openModal: openDuplicateModal,
    closeModal: closeDuplicateModal,
  } = useModal();

  const {
    isOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal();

  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const deleteMutation = useDeleteAgentVersion();

  const currentVersion = versionQuery.data;

  // Get drawer state from coordinator
  const { openDrawer, exitingDrawerId } = useSideDrawerCoordinator();
  const isAnyDrawerOpen = openDrawer !== null || exitingDrawerId !== null;

  const handleViewHistory = () => {
    navigate(
      getUrlToAgentHistoryPage(orgId, wsId, agentId, {
        [AgentHistoryKeys.VersionId]: versionId,
        [AgentHistoryKeys.Environment]:
          currentVersion?.status === "draft"
            ? DecisionEnvironment.SANDBOX
            : DecisionEnvironment.LIVE,
      }),
    );
  };

  const handlePublish = async () => {
    if (!currentVersion) return;

    try {
      await publishMutation.mutateAsync({
        agentId,
        versionId,
        etag: currentVersion.etag,
      });
      toastActions.success({
        title: "Agent version published successfully",
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (error instanceof AxiosError && error.response?.status === 422) {
        errorMessage =
          "Make sure all the flow versions you are using as tools are published";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toastActions.failure({
        title: "Failed to publish Agent version",
        description: errorMessage,
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ agentId, versionId });
      closeDeleteModal();

      // Navigate to another version if available, otherwise to the agent versions page
      const otherVersions = versionsQuery.data?.filter(
        (v) => v.id !== versionId,
      );
      if (otherVersions && otherVersions.length > 0) {
        navigate(
          getUrlToAgentVersionPage(orgId, wsId, agentId, otherVersions[0].id),
        );
      } else {
        navigate(getUrlToAgentVersionsPage(orgId, wsId, agentId));
      }

      toastActions.success({
        title: "Version deleted successfully",
      });
    } catch (error) {
      toastActions.failure({
        title: "Failed to delete version",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  };

  const getPageHeaderTitle = () => {
    if (agentQuery.isLoading || versionsQuery.isLoading) {
      return (
        <div className="flex items-center gap-x-2">
          <SkeletonPlaceholder height="h-5" width="w-36" />
          <Icon icon={faChevronRight} size="xs" />
          <SkeletonPlaceholder height="h-5" width="w-36" />
        </div>
      );
    }

    if (agentQuery.data && versionsQuery.data) {
      return (
        <div className="flex items-center gap-x-2">
          <Link
            className="flex items-center gap-x-2"
            to={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
          >
            <div className="max-w-96 truncate">{agentQuery.data.name}</div>{" "}
          </Link>
          <Icon icon={faChevronRight} size="xs" />
          <VersionPicker versions={versionsQuery.data} />
        </div>
      );
    }

    const isNotFound =
      (agentQuery.error as AxiosError)?.response?.status === 404 ||
      (versionsQuery.error as AxiosError)?.response?.status === 404;

    return isNotFound ? "Agent not found" : "Error loading Agent";
  };

  const MAX_FORM_WIDTH = 950;

  const renderMainContent = () => {
    if (versionQuery.error) {
      return (
        <AgentVersionErrorState
          error={versionQuery.error}
          refetch={versionQuery.refetch}
        />
      );
    }

    if (currentVersion) {
      return (
        <AgentVersionFormProvider
          key={currentVersion.id}
          version={currentVersion}
        >
          <AgentVersionConfigPanelProvider>
            <div className="flex h-full">
              {/* Left: Scrollable Configuration Form Area */}
              <div
                className={`flex min-w-[500px] flex-1 overflow-auto bg-gray-100 py-5 pr-5 pl-5 ${
                  isAnyDrawerOpen
                    ? "nativeScrollbarStyled"
                    : "decideScrollbar compensate-scrollbar-gutter"
                }`}
              >
                <div
                  className="mx-auto w-full"
                  style={{
                    maxWidth: isAnyDrawerOpen ? undefined : MAX_FORM_WIDTH,
                  }}
                >
                  <AgentVersionConfigPanel version={currentVersion} />
                </div>
              </div>

              {/* Right: Drawers (wrapped for smooth transitions) */}
              <DrawerContainer>
                <JsonSchemaDrawer />
                <AgentPlaygroundDrawer
                  agentId={agentId}
                  versionId={versionId}
                />
                <AddToolDrawer />
                <FlowToolConfigDrawer
                  immutable={currentVersion.status === "published"}
                />
              </DrawerContainer>
            </div>
          </AgentVersionConfigPanelProvider>
        </AgentVersionFormProvider>
      );
    }

    return (
      <div className="flex h-full">
        {/* Left: Skeleton state */}
        <div
          className={`flex min-w-[500px] flex-1 overflow-auto bg-gray-100 py-5 pr-5 pl-5 ${
            isAnyDrawerOpen
              ? "nativeScrollbarStyled"
              : "decideScrollbar compensate-scrollbar-gutter"
          }`}
        >
          <div
            className="mx-auto w-full"
            style={{
              maxWidth: isAnyDrawerOpen ? undefined : MAX_FORM_WIDTH,
            }}
          >
            <AgentVersionConfigPanelSkeleton />
          </div>
        </div>
      </div>
    );
  };

  return (
    <PlaygroundProvider agentId={agentId} versionId={versionId}>
      <DashboardContent
        Header={
          <PageHeader
            backLink={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
            sideContent={
              <div className="flex items-center gap-x-3">
                {agentQuery.isLoading || !currentVersion ? (
                  <>
                    <SkeletonPlaceholder
                      height="h-5"
                      rounded="rounded-lg"
                      width="w-36"
                    />
                    <SkeletonPlaceholder
                      height="h-7.5"
                      rounded="rounded-lg"
                      width="w-[74px]"
                    />
                    <SkeletonPlaceholder
                      height="h-5"
                      rounded="rounded-lg"
                      width="w-5"
                    />
                    <div className="-mr-1" />
                  </>
                ) : (
                  <>
                    <UpdatedInfo version={currentVersion} />
                    <TestAgentButton />
                    <PageMenu
                      configuration={currentVersion?.configuration}
                      isDraft={currentVersion?.status === "draft"}
                      isPublishing={publishMutation.isPending}
                      onDelete={openDeleteModal}
                      onDuplicate={openDuplicateModal}
                      onEdit={openEditModal}
                      onPublish={handlePublish}
                      onViewHistory={handleViewHistory}
                    />
                  </>
                )}
              </div>
            }
            title={getPageHeaderTitle()}
          />
        }
        removePadding
      >
        <div
          className="flex h-full flex-col bg-gray-100"
          data-loc="agent-version-page"
        >
          {renderMainContent()}
        </div>
        <CreateAgentVersionModal
          agentId={agentId}
          open={isDuplicateModalOpen}
          startFromScratch={false}
          versionId={versionId}
          onClose={closeDuplicateModal}
          onCreateSuccess={(createdAgentId, newVersion) =>
            navigate(
              getUrlToAgentVersionPage(
                orgId,
                wsId,
                createdAgentId,
                newVersion.id,
              ),
            )
          }
        />
        <EditAgentVersionModal
          open={isEditModalOpen}
          version={currentVersion ?? null}
          onClose={closeEditModal}
        />
        <DeleteAgentVersionModal
          isOpen={isDeleteModalOpen}
          version={currentVersion}
          workspaceId={wsId}
          onClose={closeDeleteModal}
          onConfirm={handleConfirmDelete}
        />
      </DashboardContent>
    </PlaygroundProvider>
  );
};

const AgentVersionErrorState: React.FC<{
  error: Error;
  refetch: () => void;
}> = ({ refetch, error }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const isNotFoundError =
    error instanceof AxiosError && error?.response?.status === 404;

  if (isNotFoundError) {
    return (
      <div className="m-auto flex w-[1084px] flex-1 gap-x-5 bg-gray-50 py-10">
        <WhitePane>
          <EmptyState
            actionButton={{
              text: "Go to Agents",
              onClick: () => navigate(getUrlToAgentsPage({ orgId, wsId })),
            }}
            description="The Agent you are looking for does not exist."
            headline="Agent not found"
            icon={faRotateRight}
            variant="error"
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <div className="m-auto flex w-[1084px] flex-1 gap-x-5 bg-gray-50 py-10">
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
          }}
          description="We're having trouble loading Agent Version. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading Agent Version"
          icon={faRotateRight}
          variant="error"
        />
      </WhitePane>
    </div>
  );
};

type VersionPickerProps = {
  versions: AgentVersion[];
};

const VersionPicker: React.FC<VersionPickerProps> = ({ versions }) => {
  const navigate = useNavigate();
  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();
  const { sessionState, stopSession } = usePlaygroundContext();
  const {
    isOpen: isSwitchModalOpen,
    data: pendingVersionId,
    openModal: openSwitchModal,
    closeModal: closeSwitchModal,
    afterLeave: afterSwitchModalLeave,
  } = useModal<string>();

  const handleVersionSelect = useCallback(
    (selectedVersionId: string) => {
      if (selectedVersionId === versionId) return;

      if (sessionState.status === "active") {
        openSwitchModal(selectedVersionId);
      } else {
        navigate(
          getUrlToAgentVersionPage(orgId, wsId, agentId, selectedVersionId),
        );
      }
    },
    [
      versionId,
      sessionState.status,
      openSwitchModal,
      navigate,
      orgId,
      wsId,
      agentId,
    ],
  );

  const handleConfirmSwitch = useCallback(() => {
    if (!pendingVersionId) return;
    stopSession();
    navigate(getUrlToAgentVersionPage(orgId, wsId, agentId, pendingVersionId));
    closeSwitchModal();
  }, [
    pendingVersionId,
    stopSession,
    navigate,
    orgId,
    wsId,
    agentId,
    closeSwitchModal,
  ]);

  return (
    <>
      <FixedPositionedDropdown
        buttonClassName="rounded-full border-none max-w-64"
        dataLoc="current-version-picker"
        elements={[...versions]
          .sort(
            (a, b) =>
              new Date(b.metadata.updated_at).getTime() -
              new Date(a.metadata.updated_at).getTime(),
          )
          .map((version) => ({
            key: version.id,
            value: version,
          }))}
        placement="bottom start"
        renderButtonValue={(version) =>
          version ? (
            <Pill
              dataLoc="current-version-picker-button"
              size="lg"
              variant="outlined-white"
              fullWidth
            >
              <AgentVersionStatusIndicator status={version.status} />
              <Pill.Text>{version.name}</Pill.Text>
              <Pill.Icon icon={faChevronDown} size="base" />
            </Pill>
          ) : (
            <SkeletonPlaceholder height="h-5" width="w-36" />
          )
        }
        renderValue={({ value: version, selected }) => (
          <div className="flex items-center justify-between gap-x-2">
            <div className="flex items-center gap-x-1.5">
              <AgentVersionStatusIndicator status={version.status} />
              <span>{version.name}</span>
            </div>
            {selected && <Icon icon={faCheck} size="sm" variant="active" />}
          </div>
        )}
        selected={versionId}
        onSelect={handleVersionSelect}
      />
      <ConfirmationModal
        afterLeave={afterSwitchModalLeave}
        description="Switching versions will stop the current test. The current test progress will be lost."
        open={isSwitchModalOpen}
        title="Switch Agent version?"
        onClose={closeSwitchModal}
        onConfirm={handleConfirmSwitch}
      />
    </>
  );
};

const UpdatedInfo: React.FC<{
  version: AgentVersion;
}> = ({ version }) => {
  const isSaving = !!useIsMutating({
    mutationKey: AGENT_VERSION_CONFIGURATION_MUTATION_KEY,
  });

  const isPublished = version.status === "published";
  const dateValue = isPublished
    ? version.metadata.published_at
    : version.metadata.updated_at;
  const prefix = isPublished ? "Published" : "Updated";

  if (!dateValue) {
    return null;
  }

  const variant = isPublished ? "green" : "indigo-light";
  const overlayBgClass = isPublished ? "bg-green-50" : "bg-indigo-50";
  const overlayTextClass = isPublished ? "text-green-700" : "text-indigo-500";
  const overlayIconVariant = isPublished ? "success" : "active";

  return (
    <Pill dataLoc="last-updated-info" variant={variant}>
      <div className="relative flex items-center">
        <Pill.Icon icon={faCheck} />
        <Pill.Text fontType="text">
          {prefix} <DisplayDateFromNow value={dateValue} />
        </Pill.Text>
        <AnimatePresence>
          {isSaving && (
            <m.div
              animate={{ opacity: 1 }}
              className={`absolute inset-0 flex items-center ${overlayBgClass}`}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Icon
                variant={overlayIconVariant}
                icon={faSpinner}
                size="2xs"
                spin
              />
              <span
                className={`mx-1 font-inter-medium-12px ${overlayTextClass}`}
              >
                Saving changes...
              </span>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </Pill>
  );
};

const PageMenu: React.FC<{
  isDraft?: boolean;
  isPublishing?: boolean;
  configuration?: AgentVersionConfigurationOutput;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onViewHistory: () => void;
}> = ({
  isDraft,
  isPublishing,
  configuration,
  onDelete,
  onDuplicate,
  onEdit,
  onPublish,
  onViewHistory,
}) => {
  const options = useAgentVersionMenuOptions({
    isDraft: isDraft ?? false,
    configuration,
    onEdit,
    onDuplicate,
    onViewHistory,
    onPublish,
    onDelete,
    isPublishing,
  });

  return (
    <div className="pr-2">
      <EllipsisOptionsDropdown dataLoc="version-page-menu" elements={options} />
    </div>
  );
};

/**
 * Pulsing progress bar shown in the Test button when a test is running.
 * Exported for use by AgentPlayground's portal.
 */
export const PulsingProgressBar: React.FC = () => (
  <m.div
    key="pulsing-progress-bar"
    animate={{ width: 44 }}
    className="relative h-[3px] overflow-hidden rounded-full bg-white/20"
    exit={{ width: 18 }}
    initial={{ width: 18 }}
    role="progressbar"
  >
    <m.div
      animate={{ x: ["-100%", "100%"] }}
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
      transition={{ repeat: Infinity, duration: 1, ease: "easeInOut" }}
    />
  </m.div>
);

/**
 * Test Agent button that handles three states:
 * 1. Playground closed → "Test agent" button (opens playground)
 * 2. Playground open, not running → "Start test" button (starts test)
 * 3. Running → "Testing..." button with progress bar and stop button
 *
 * Uses a single persistent wrapper so framer-motion can smoothly
 * animate width and content transitions between states.
 */
const TestAgentButton: React.FC = () => {
  const { requestOpen, requestClose, openDrawer } = useSideDrawerCoordinator();
  const { sessionState, stopSession, startTest, invoke } =
    usePlaygroundContext();

  const isPlaygroundOpen = openDrawer?.type === "playground";
  const isRunning = sessionState.status === "active";
  const isInvokePending = invoke.isPending;

  const handleToggleDrawer = useCallback(() => {
    if (isPlaygroundOpen) {
      requestClose();
    } else {
      requestOpen({ type: "playground" });
    }
  }, [isPlaygroundOpen, requestClose, requestOpen]);

  const handleClick = () => {
    if (!isPlaygroundOpen && !isRunning) {
      requestOpen({ type: "playground" });
    } else if (isRunning) {
      handleToggleDrawer();
    } else {
      startTest();
    }
  };

  const idleText = isPlaygroundOpen ? "Start test" : "Test Agent";
  const idleWidth = isPlaygroundOpen ? "105px" : "112px";
  const buttonText = isRunning ? "Testing..." : idleText;

  const button = (
    <m.div
      animate={{ width: isRunning ? "165px" : idleWidth }}
      className="flex cursor-pointer items-center overflow-hidden rounded-lg bg-indigo-600 font-inter-normal-13px text-white"
      data-loc="test-agent-button"
      initial={false}
      onClick={handleClick}
    >
      <div className="flex shrink-0 items-center py-1.5 pr-2 pl-3">
        <div className="relative mr-1 flex items-center">
          <AnimatePresence initial={false} mode="wait">
            {isRunning ? (
              <PulsingProgressBar key="progress-bar" />
            ) : (
              <m.div
                key="play-icon"
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
              >
                <Icon icon={faPlay} size="2xs" variant="inverted" />
              </m.div>
            )}
          </AnimatePresence>
        </div>
        <span className="overflow-hidden font-inter-medium-13px">
          {buttonText}
        </span>
      </div>
      <div
        className={twJoin(
          "shrink-0 py-1.5 pr-3 pl-1",
          isInvokePending ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          !isRunning && "opacity-0",
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (!isInvokePending) {
            stopSession();
          }
        }}
      >
        <div className="flex items-center border-indigo-500 border-l py-0.5 pl-1.5">
          <Icon icon={faTimes} size="2xs" variant="inverted" />
        </div>
      </div>
    </m.div>
  );
  return (
    <Tooltip
      body="Run the Agent in a safe environment to test Agent behavior, tool usage, and outputs without affecting live flows or customer decisions."
      delay={500}
      disabled={isPlaygroundOpen || isRunning}
      placement="bottom-end"
      title="Test your Agent"
      asChild
    >
      {button}
    </Tooltip>
  );
};
